import { CheckIcon, ChevronRightIcon, MailIcon } from 'lucide-react';
import { useSuspenseQuery } from '@apollo/client/react';
import { Link } from 'react-router';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AppRequestsDocument } from 'src/graphql/actions/appRequests/appRequests.query';
import { withSuspense } from 'src/hoc';
import { AppRequestsKindEnum } from 'src/graphql/schema';
import { pluralize } from 'src/utils/pluralize';
import { cn } from 'src/lib/utils';

import { PendingVendorRequestsBannerSkeleton } from './PendingVendorRequestsBannerSkeleton';

const PendingVendorRequestsBanner = () => {
  const {
    data: { appRequests: receivedRequests },
  } = useSuspenseQuery(AppRequestsDocument, {
    variables: { kind: AppRequestsKindEnum.Received, perPage: 0 },
  });
  const {
    data: { appRequests: sentRequests },
  } = useSuspenseQuery(AppRequestsDocument, {
    variables: { kind: AppRequestsKindEnum.Sent, perPage: 0 },
  });

  const hasRequests = receivedRequests.nodesCount || sentRequests.nodesCount;

  return (
    <Alert
      className={cn('grid-cols-[auto_1fr_auto] items-start gap-x-4 p-4', hasRequests && 'border-sky-200 bg-sky-100')}
    >
      <span
        className={cn(
          'flex size-8 shrink-0 items-center justify-center rounded-md bg-muted',
          hasRequests ? 'size-6.5 rounded-full bg-sky-500' : 'border border-border',
        )}
      >
        {hasRequests ? <MailIcon className="size-3.5 text-white" /> : <CheckIcon className="size-4 text-slate-400" />}
      </span>
      <div className="col-start-2">
        <AlertTitle className="text-sm font-extrabold">
          {hasRequests
            ? pluralize(sentRequests.nodesCount, 'pending vendor request', 'pending vendor requests')
            : 'No pending vendor requests'}
        </AlertTitle>
        <AlertDescription className={cn(!hasRequests && 'text-xs')}>
          {hasRequests
            ? pluralize(receivedRequests.nodesCount, 'needs your response', 'need your response')
            : "You're all caught up. Requests you send or receive show up here."}
        </AlertDescription>
      </div>
      <div className="col-start-2 sm:col-start-3">
        <Button variant={hasRequests ? 'secondary' : 'ghost'} asChild>
          <Link to="vendor-requests">
            View requests
            {!hasRequests && <ChevronRightIcon />}
          </Link>
        </Button>
      </div>
    </Alert>
  );
};

export default withSuspense(PendingVendorRequestsBanner, PendingVendorRequestsBannerSkeleton);
